import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Image } from '../../../../../models/image';
import { InteractionService } from '../../../../../services/interaction.service';
import { SynchronizationService } from '../../../../../services/synchronization.service';
import interact from 'interactjs';
import { CommunicationService } from '../../../../../services/communication.service';
import { Subscription } from 'rxjs';
import { AwallComponentEvent } from '../../../../../models/awallComponentEvent';
import { TaskboardViewComponent } from '../../../../../actionviews/taskboard-view/taskboard-view.component';
import { IssueComponent } from '../../../../issue/issue.component';

@Component({
  selector: 'app-cloned-image',
  templateUrl: './cloned-image.component.html',
  styleUrls: ['./cloned-image.component.scss']
  })
export class ClonedImageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('divcontainer', { static: true }) element: ElementRef;
  private elementOriginHeight;
  private elementOriginWidth;
  private event: Subscription[] = [];
  defaultImage = false;

  private classDragFromGallery = 'dragfromgallery';
  private classDraggable = 'draggableimage';
  private classOnDrag = 'ondragging';
  private classResizable = 'resizableimage';
  private classDroppable = 'droppableimage';
  private classDroptarget = 'droptarget';
  private classImageDropzone = 'imagedropzone';

  image: Image;
  uniqueId: string;
  @Output() initEnd: EventEmitter<any> = new EventEmitter();

  constructor(
    private interactionService: InteractionService,
    private synchronizationService: SynchronizationService,
    private communicationService: CommunicationService
  ) { }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.initializeClonedImage();
    this.setupDraggableImage();
    this.setupRealtimeEvents();
    this.setupResizableImage();
    this.initEnd.emit();
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
    const dropzones = document.getElementsByClassName(this.classImageDropzone);
    for (let i = 0; i < dropzones.length; i++) {
      dropzones[i].classList.remove(`imagedropzone${this.uniqueId}`);
    }
  }

  /**
   * Initializes the Component after it has been cloned.
   */
  private initializeClonedImage() {
    this.element.nativeElement.classList.add(this.classDragFromGallery);
    this.element.nativeElement.classList.add(this.classOnDrag);
    this.element.nativeElement.setAttribute('uuid', this.uniqueId);
    this.elementOriginWidth = this.element.nativeElement.offsetWidth;
    this.elementOriginHeight = this.element.nativeElement.offsetHeight;
  }

  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealtimeEvents() {
    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}StartResizeImage`)
      .subscribe(size => this.element.nativeElement.classList.add(this.classResizable)));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}ResizeImage`)
      .subscribe(data => {
        this.element.nativeElement.style.webkitTransform = `matrix(${data.size.scale}, 0, 0, ${data.size.scale}, 0, 0)`;
        this.element.nativeElement.style.transform = `matrix(${data.size.scale}, 0, 0, ${data.size.scale}, 0, 0)`;
        this.element.nativeElement.setAttribute('data-scale', data.size.scale);
      }));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}ResetImage`)
      .subscribe(() => this.resetImageResize(this.element.nativeElement)));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}CloseImage`)
      .subscribe(() => this.element.nativeElement.parentElement.removeChild(this.element.nativeElement)));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}StartMoveImage`)
      .subscribe(() => this.element.nativeElement.classList.add(this.classOnDrag)));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}MoveImage`)
      .subscribe(position => {
        // translate the element
        const target = this.element.nativeElement;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);

        target.style.webkitTransform = `matrix(${scale}, 0, 0, ${scale}, ${position.x}, ${position.y})`;
        target.style.transform = `matrix(${scale}, 0, 0, ${scale}, ${position.x}, ${position.y})`;

        // update the posiion attributes
        target.setAttribute('data-x', `${position.x}`);
        target.setAttribute('data-y', `${position.y}`);
      }));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}EndMoveImage`)
      .subscribe(() => this.element.nativeElement.classList.remove(this.classOnDrag)));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}MoveImageFromGallery`)
      .subscribe(position => {
        // translate the element
        const target = this.element.nativeElement;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);

        target.style.webkitTransform = `matrix(${scale}, 0, 0, ${scale}, ${position.x}, ${position.y})`;
        target.style.transform = `matrix(${scale}, 0, 0, ${scale}, ${position.x}, ${position.y})`;

        // update the posiion attributes
        target.setAttribute('data-x', `${position.x}`);
        target.setAttribute('data-y', `${position.y}`);
      }));

    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryDraggableImage${this.uniqueId}EndMoveImageFromGallery`)
      .subscribe(() => this.endMoveImageInteractionFromGallery(this.element.nativeElement)));

    this.synchronizationService.subscribeEvent<string>(`GalleryDraggableImage${this.uniqueId}DragDropTask`)
      .subscribe(selector => {
        document.querySelector(selector).classList.remove(this.classDroptarget);
        this.element.nativeElement.parentElement.removeChild(this.element.nativeElement);
      });

    this.synchronizationService.subscribeEvent<string>(`GalleryDraggableImage${this.uniqueId}DragDropUserstory`)
      .subscribe(selector => {
        document.querySelector(selector).classList.remove(this.classDroptarget);
        this.element.nativeElement.parentElement.removeChild(this.element.nativeElement);
      });

    this.synchronizationService.subscribeEvent<string>(`GalleryDraggableImage${this.uniqueId}DropzoneEnter`)
      .subscribe(selector => document.querySelector(selector).classList.add(this.classDroptarget));

    this.synchronizationService.subscribeEvent<string>(`GalleryDraggableImage${this.uniqueId}DropzoneLeave`)
      .subscribe(selector => document.querySelector(selector).classList.remove(this.classDroptarget));
  }

  /**
   * Set up the drag interactions on the image.
   */
  setupDraggableImage() {
    const dropzones = document.getElementsByClassName(this.classImageDropzone);
    for (let i = 0; i < dropzones.length; i++) {
      dropzones[i].classList.add(`${this.classImageDropzone}${this.uniqueId}`);
    }
    // Drop on imagedropzone
    // Draggable draggableimage
    // Interactions are executed only if the image is not resized
    interact(`.${this.classImageDropzone}${this.uniqueId}`).dropzone({
      // only accept elements matching this CSS selector
      accept: `.${this.classDroppable}${this.uniqueId}`,
      // Require a 50% element overlap for a drop to be possible
      overlap: 0.20,
      // listen for drop related events:
      ondropactivate: (event) => {
      },
      ondragenter: (event) => {
        if (!event.relatedTarget.classList.contains(this.classResizable)) {
          event.target.classList.add(this.classDroptarget);
          if (event.target.hasAttribute('data-taskcardid')) {
            const taskId = event.target.getAttribute('data-taskcardid');
            this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}DropzoneEnter`,
              `[data-taskcardid="${taskId}"]`);
          }
          if (event.target.hasAttribute('data-userstoryid')) {
            const storyId = event.target.getAttribute('data-userstoryid');
            this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}DropzoneEnter`,
              `[data-userstoryid="${storyId}"]`);
          }
        }
      },
      ondragleave: (event) => {
        if (!event.relatedTarget.classList.contains(this.classResizable)) {
          event.target.classList.remove(this.classDroptarget);
          if (event.target.hasAttribute('data-taskcardid')) {
            const taskId = event.target.getAttribute('data-taskcardid');
            this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}DropzoneLeave`,
              `[data-taskcardid="${taskId}"]`);
          }
          if (event.target.hasAttribute('data-userstoryid')) {
            const storyId = event.target.getAttribute('data-userstoryid');
            this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}DropzoneLeave`,
              `[data-userstoryid="${storyId}"]`);
          }
        }
      },

      ondrop: (event) => {
        const draggedImage = event.relatedTarget;

        if (!draggedImage.classList.contains(this.classResizable)) {
          event.target.classList.remove(this.classDroptarget);

          if (event.target.hasAttribute('data-taskcardid')) {
            this.sendImageDroppedEvents(event.target.getAttribute('data-taskcardid'), true);
            draggedImage.parentElement.removeChild(draggedImage);
          }
          if (event.target.hasAttribute('data-userstoryid')) {
            this.sendImageDroppedEvents(event.target.getAttribute('data-userstoryid'), false);
            draggedImage.parentElement.removeChild(draggedImage);
          }
        }
      }
    });

    // drag image after hold
    this.interactionService.addDragInteraction(`.readytodrag${this.uniqueId}`,
      (event) => {
        // ondragging class is added on initialization that it can be used after 'hold' interaction
      },
      (position) => {
        this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}MoveImageFromGallery`, position);
      },
      (event) => {
        this.endMoveImageInteractionFromGallery(event.target);
        event.target.classList.remove(`readytodrag${this.uniqueId}`);
        this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}EndMoveImageFromGallery`);
      },
      true
    );

    // drag image
    this.interactionService.addDragInteraction(`.${this.classDraggable}${this.uniqueId}`,
      (event) => {
        event.target.classList.add(this.classOnDrag);
        this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}StartMoveImage`);
      },
      (position) => {
        this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}MoveImage`, position);
      },
      (event) => {
        event.target.classList.remove(this.classOnDrag);
        this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}EndMoveImage`);
      },
      false
    );
  }

  /**
   * Sends event to the component who needs to react to the dropped image and to other remote clients
   * @param issueId the id of the issue the image was dropped on
   * @param isSubIssue true if the image was dropped on a subtask, false if it was on a userstory or other non-subissue type
   */
  sendImageDroppedEvents(issueId: number, isSubIssue: boolean) {
    // Informing other components
    const eventPayload = { 'issueId': issueId, 'image': this.image };
    const componentEvent = new AwallComponentEvent('image-dropped', eventPayload);
    this.communicationService.sendData(TaskboardViewComponent.name, componentEvent);
    this.communicationService.sendData(IssueComponent.name, componentEvent);

    // Informing other clients
    const idDataFieldName = (isSubIssue) ? `[data-taskcardid="${issueId}"]` : `[data-userstoryid="${issueId}"]`;
    const eventNamePart = (isSubIssue) ? 'DragDropTask' : 'DragDropUserstory';
    this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}${eventNamePart}`, idDataFieldName);
  }

  /**
   * Set up the resize interactions on the image.
   */
  setupResizableImage() {
    const html: HTMLElement = this.element.nativeElement;
    this.elementOriginWidth = html.offsetWidth;
    this.elementOriginHeight = html.offsetHeight;

    this.interactionService.addPinchToZoomInteraction(`.${this.classDraggable}${this.uniqueId}`, (event) => {
      this.element.nativeElement.classList.add(this.classResizable);
      this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}StartResizeImage`);
    }, (size) => {
      if (size.scale > 6) {
        size.scale = 6;
      } else if (size.scale < 0.9) {
        size.scale = 0.9;
      }
      this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}ResizeImage`, {
        size: size
      });
    });
  }

  /**
   * Ends the move interaction when dragging an image from the gallery.
   * @param target The target Html Element of the moved image.
   */
  private endMoveImageInteractionFromGallery(target: HTMLElement) {
    target.classList.remove(`readytodrag${this.image.id}`);
    target.classList.remove(this.classDragFromGallery);
    target.classList.remove(this.classOnDrag);
    target.classList.add(this.classDraggable);
    target.classList.add(`${this.classDraggable}${this.uniqueId}`);
  }

  /**
   * Closes an image respectively removes the component such that it will be destroyed.
   * @param event Fired event from the target element.
   */
  closeImage(event: any) {
    const closest = event.target.closest(`.${this.classDraggable}`);
    closest.parentElement.removeChild(closest);
    this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}CloseImage`);
  }

  /**
   * Resets the image.
   * @param event Fired event from the target element.
   */
  resetImage(event: any) {
    const target = event.target.closest(`.${this.classResizable}`);
    this.resetImageResize(target);
    this.synchronizationService.sendRealTimeEvent(`GalleryDraggableImage${this.uniqueId}ResetImage`);
  }

  /**
   * Resets the pinchToResize on the image.
   * @param target The target Html Element which should be reset.
   */
  private resetImageResize(target: HTMLElement) {
    this.interactionService.undoPinchToZoomInteraction(target);
    target.classList.remove(this.classResizable);
  }

}
